/**
 * One Firestore onSnapshot per match document; fan-out to registry/teams/history/etc.
 */

/** @type {{ ensureMatchReadable?: (matchId: string) => Promise<object|null>, subscribeMatch?: Function } | null} */
let deps = null;

/** @type {Map<string, { refCount: number, unsub: (() => void) | null, ensureDone: boolean, lastDoc: object | null, listeners: { match: Set<Function>, registry: Set<Function>, members: Set<Function>, players: Set<Function>, teams: Set<Function>, history: Set<Function> } }>} */
const hubs = new Map();

export function initMatchSubscriptionHub({ ensureMatchReadable, subscribeMatch }) {
  deps = { ensureMatchReadable, subscribeMatch };
}

function dispatch(matchId, doc) {
  const hub = hubs.get(matchId);
  if (!hub || !doc) return;
  hub.lastDoc = doc;
  hub.listeners.match.forEach((fn) => {
    try {
      fn(doc);
    } catch {
      /* consumer */
    }
  });
  const registryEntries = doc.registryEntries || [];
  const roomMembers = doc.roomMembers || [];
  const teams = doc.teams || [];
  const history = doc.history || [];
  hub.listeners.registry.forEach((fn) => {
    try {
      fn(registryEntries);
    } catch {
      /* consumer */
    }
  });
  hub.listeners.members.forEach((fn) => {
    try {
      fn(roomMembers);
    } catch {
      /* consumer */
    }
  });
  hub.listeners.players.forEach((fn) => {
    try {
      fn(registryEntries);
    } catch {
      /* consumer */
    }
  });
  hub.listeners.teams.forEach((fn) => {
    try {
      fn(teams);
    } catch {
      /* consumer */
    }
  });
  hub.listeners.history.forEach((fn) => {
    try {
      fn(history);
    } catch {
      /* consumer */
    }
  });
}

function attachFirestoreListener(matchId) {
  const hub = hubs.get(matchId);
  if (!hub || hub.unsub || !deps?.subscribeMatch) return;
  hub.unsub = deps.subscribeMatch(matchId, (doc) => {
    if (doc) dispatch(matchId, doc);
  });
}

function startEnsure(matchId) {
  const hub = hubs.get(matchId);
  if (!hub || hub.ensureDone || !deps?.ensureMatchReadable) return;
  hub.ensureDone = true;
  deps.ensureMatchReadable(matchId).then((doc) => {
    if (doc) dispatch(matchId, doc);
  });
}

function releaseHub(matchId) {
  const hub = hubs.get(matchId);
  if (!hub) return;
  hub.refCount -= 1;
  if (hub.refCount > 0) return;
  hub.unsub?.();
  hubs.delete(matchId);
}

/**
 * @param {string} matchId
 * @param {{ onMatch?: (doc: object) => void, onRegistry?: (entries: unknown[]) => void, onMembers?: (members: unknown[]) => void, onPlayers?: (players: unknown[]) => void, onTeams?: (teams: unknown[]) => void, onHistory?: (history: unknown[]) => void }} handlers
 * @returns {() => void}
 */
export function subscribeToMatchDocument(matchId, handlers) {
  if (!matchId || !deps) {
    return () => {};
  }

  let hub = hubs.get(matchId);
  if (!hub) {
    hub = {
      refCount: 0,
      unsub: null,
      ensureDone: false,
      lastDoc: null,
      listeners: {
        match: new Set(),
        registry: new Set(),
        members: new Set(),
        players: new Set(),
        teams: new Set(),
        history: new Set(),
      },
    };
    hubs.set(matchId, hub);
  }

  const added = [];
  if (handlers.onMatch) {
    hub.listeners.match.add(handlers.onMatch);
    added.push(["match", handlers.onMatch]);
  }
  if (handlers.onRegistry) {
    hub.listeners.registry.add(handlers.onRegistry);
    added.push(["registry", handlers.onRegistry]);
  }
  if (handlers.onMembers) {
    hub.listeners.members.add(handlers.onMembers);
    added.push(["members", handlers.onMembers]);
  }
  if (handlers.onPlayers) {
    hub.listeners.players.add(handlers.onPlayers);
    added.push(["players", handlers.onPlayers]);
  }
  if (handlers.onTeams) {
    hub.listeners.teams.add(handlers.onTeams);
    added.push(["teams", handlers.onTeams]);
  }
  if (handlers.onHistory) {
    hub.listeners.history.add(handlers.onHistory);
    added.push(["history", handlers.onHistory]);
  }

  hub.refCount += 1;
  startEnsure(matchId);
  attachFirestoreListener(matchId);

  // Late subscribers reuse the last fan-out (no extra Firestore read / loading flash).
  if (hub.lastDoc) {
    const replay = {
      onMatch: handlers.onMatch,
      onRegistry: handlers.onRegistry,
      onMembers: handlers.onMembers,
      onPlayers: handlers.onPlayers,
      onTeams: handlers.onTeams,
      onHistory: handlers.onHistory,
    };
    const doc = hub.lastDoc;
    try {
      if (replay.onMatch) replay.onMatch(doc);
      if (replay.onRegistry) replay.onRegistry(doc.registryEntries || []);
      if (replay.onMembers) replay.onMembers(doc.roomMembers || []);
      if (replay.onPlayers) replay.onPlayers(doc.registryEntries || []);
      if (replay.onTeams) replay.onTeams(doc.teams || []);
      if (replay.onHistory) replay.onHistory(doc.history || []);
    } catch {
      /* consumer */
    }
  }

  return () => {
    const current = hubs.get(matchId);
    if (!current) return;
    added.forEach(([type, fn]) => {
      current.listeners[type].delete(fn);
    });
    releaseHub(matchId);
  };
}

/** Test-only: reset hub state */
export function __resetMatchSubscriptionHubForTests() {
  hubs.forEach((hub) => hub.unsub?.());
  hubs.clear();
}

/** Test-only: count active Firestore listeners */
export function __activeMatchListenerCountForTests() {
  let n = 0;
  hubs.forEach((hub) => {
    if (hub.unsub) n += 1;
  });
  return n;
}
